import AccountContext from 'context/AccountContext';
import useVisibleWalletTypes, {
  WALLET_TYPES,
} from 'hooks/useVisibleWalletTypes';
import { useContext, useEffect } from 'react';
import {
  isAdyenWalletSlotLocallyOccupied,
  usePaymentStore,
} from 'stores/paymentStore';
import type { AdyenWalletType } from 'types/generatedSchemaTypes';
import {
  ADYEN_WALLET_ROWS,
  type AdyenWalletRowDescriptor,
  hasActiveAdyenWalletOfType,
} from 'utils/paymentUtils';

export type WalletAddRow = AdyenWalletRowDescriptor & {
  walletType: AdyenWalletType;
  alreadyAdded: boolean;
};

// One row per wallet type the current user may add. `alreadyAdded` covers an
// Active/Verifying/Blocked/Adding wallet of that type as well as a
// locally occupied slot, so a row is never offered twice.
const useWalletAddRows = (): WalletAddRow[] => {
  const {
    customer: { paymentCards },
  } = useContext(AccountContext);
  const visibleWalletTypes = useVisibleWalletTypes();
  const adyenWalletSlotOccupiedAt = usePaymentStore(
    (state) => state.adyenWalletSlotOccupiedAt
  );
  const clearAdyenWalletSlot = usePaymentStore(
    (state) => state.clearAdyenWalletSlot
  );

  // Drop local occupancy once customer paymentCards already occupy the slot.
  useEffect(() => {
    for (const walletType of WALLET_TYPES) {
      if (
        adyenWalletSlotOccupiedAt[walletType] != null &&
        hasActiveAdyenWalletOfType(paymentCards, walletType)
      ) {
        clearAdyenWalletSlot(walletType);
      }
    }
  }, [adyenWalletSlotOccupiedAt, clearAdyenWalletSlot, paymentCards]);

  return visibleWalletTypes.map((walletType) => ({
    walletType,
    alreadyAdded:
      hasActiveAdyenWalletOfType(paymentCards, walletType) ||
      isAdyenWalletSlotLocallyOccupied(adyenWalletSlotOccupiedAt, walletType),
    ...ADYEN_WALLET_ROWS[walletType],
  }));
};

export default useWalletAddRows;
